// src/pages/Contacts/index.js — página padrão do painel (cabeçalho, filtros, tabela do tema)
import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import LanguageIcon from "@material-ui/icons/Language";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import ChatOutlinedIcon from "@material-ui/icons/ChatOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AddIcon from "@material-ui/icons/Add";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Skeleton from "@material-ui/lab/Skeleton";
import { Menu, MenuItem } from "@material-ui/core";
import { ArrowDropDown, Backup, ContactPhone, DeleteSweep } from "@material-ui/icons";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { v4 as uuidv4 } from "uuid";

import api from "../../services/api";
import EmptyState from "../../components/EmptyState";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ContactDeleteConfirmModal from "../../components/ContactDeleteConfirmModal";
import ContactImportWpModal from "../../components/ContactImportWpModal";
import NewTicketModal from "../../components/NewTicketModal";
import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import {
  usePanelStyles,
  ModalTitle,
  DangerButton,
  FilterBar,
  SearchField,
  TableCard,
  RowActions,
} from "../../components/FormFields/panel";
import { Can } from "../../components/Can";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { getTokens, brandAlpha } from "../../styles/tokens";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "BULK_DELETE_CONTACTS") {
    const contactIds = action.payload;
    return state.filter((contact) => !contactIds.includes(contact.id));
  }

  if (action.type === "DELETE_ALL_CONTACTS") {
    const { excludeIds = [] } = action.payload;
    return state.filter((contact) => excludeIds.includes(contact.id));
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "SET_TOTAL_COUNT") {
    // Não altera o estado, mas permite rastrear o total
    return state;
  }
};

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", Icon: WhatsApp },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "webchat", label: "Webchat", Icon: LanguageIcon },
];

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    /* ---------- filtros ---------- */
    filterSelect: {
      width: 190,
      [theme.breakpoints.down("xs")]: { flex: "1 1 150px", width: "auto", minWidth: 0 },
    },
    placeholder: { color: t.textMuted },
    selectValue: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      "& > span:last-child": { overflow: "hidden", textOverflow: "ellipsis" },
      "& svg": { fontSize: 18, color: t.textSecondary, flex: "none" },
    },
    optionItem: {
      gap: 8,
      "& .MuiCheckbox-root": { padding: 4, marginLeft: -6 },
      "& svg": { fontSize: 18, color: t.textSecondary },
    },

    /* ---------- seleção em lote ---------- */
    selectionBar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      padding: "8px 10px 8px 14px",
      marginBottom: 14,
      borderRadius: t.radius.md,
      backgroundColor: t.tint.soft,
      border: `1px solid ${brandAlpha(t.brand, 0.25)}`,
    },
    selectionText: {
      flex: "1 1 180px",
      minWidth: 0,
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      color: t.text,
    },

    /* ---------- tabela ---------- */
    table: {
      minWidth: 940,
      "& .MuiTableCell-root": { whiteSpace: "nowrap" },
      // linha com avatar: 8 px de respiro bastam para ficar em ~51 px
      "& .MuiTableBody-root .MuiTableCell-root": { paddingTop: 8, paddingBottom: 8 },
    },
    checkboxCell: {
      width: 48,
      paddingRight: 0,
      "& .MuiCheckbox-root": { padding: 6 },
    },
    contactCell: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
    },
    avatar: {
      width: 34,
      height: 34,
      fontSize: 13,
      cursor: "pointer",
      flex: "none",
    },
    contactName: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.35,
      color: t.text,
      maxWidth: 260,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    contactPhone: {
      fontSize: 13,
      lineHeight: 1.35,
      color: t.textSecondary,
      fontVariantNumeric: "tabular-nums",
    },
    ellipsis: {
      display: "inline-block",
      maxWidth: 220,
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "bottom",
    },
    muted: { color: t.textMuted },
    tagsCell: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "nowrap",
    },
    tagChip: { maxWidth: 140 },
    channel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      "& svg": { fontSize: 18, color: t.textSecondary },
    },

    /* ---------- menus ---------- */
    menuItem: {
      gap: 10,
      "& svg": { fontSize: 18, color: t.textSecondary },
    },
    menuItemDanger: {
      color: t.mode === "dark" ? t.status.down.fg : t.semantic.error,
      "& svg": { color: "inherit" },
    },

    /* ---------- foto do contato ---------- */
    imageBody: { display: "flex", justifyContent: "center" },
    profileImage: {
      width: "100%",
      height: "auto",
      maxWidth: 400,
      maxHeight: 400,
      objectFit: "contain",
      borderRadius: t.radius.md,
    },
    imageFallback: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: 200,
      color: t.textMuted,
      fontSize: 14,
    },
  };
});

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const Contacts = () => {
  const classes = useStyles();
  const p = usePanelStyles();
  const theme = useTheme();
  const tokens = getTokens(theme.palette.type, theme.palette.primary.main);
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [totalContactsCount, setTotalContactsCount] = useState(0);

  // Estados para seleção múltipla
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false); // true = todos da empresa, false = apenas da página

  const [importContactModalOpen, setImportContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [ImportContacts, setImportContacts] = useState(null);
  const [blockingContact, setBlockingContact] = useState(null);
  const [unBlockingContact, setUnBlockingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportContact, setExportContact] = useState(false);
  const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});

  // Estados para exclusão múltipla com confirmação tipada
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // 'selected' ou 'all'

  const fileUploadRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const { setCurrentTicket } = useContext(TicketsContext);

  // Filtros visuais (chips)
  const [tagOptions, setTagOptions] = useState([]);
  const [walletOptions, setWalletOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null); // whatsapp | instagram | facebook | null
  const [walletFilter, setWalletFilter] = useState(null); // userId | null

  // Menu "⋮" da linha (bloquear/desbloquear e excluir)
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, contact: null });
  const closeRowMenu = () => setRowMenu((prev) => ({ ...prev, anchorEl: null }));

  // Estados para o modal de imagem
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedContactName, setSelectedContactName] = useState("");

  const { getAll: getAllSettings } = useCompanySettings();
  const [hideNum, setHideNum] = useState(false);
  const [enableLGPD, setEnableLGPD] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const settingList = await getAllSettings(user.companyId);

      for (const [key, value] of Object.entries(settingList)) {
        if (key === "enableLGPD") setEnableLGPD(value === "enabled");
        if (key === "lgpdHideNumber") setHideNum(value === "enabled");
      }
    }
    fetchData();
  }, []);

  // Opções dos chips de filtro (tags e carteiras)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/tags/list");
        if (active) setTagOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/users/list");
        if (active) setWalletOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        // carteiras são opcionais no filtro; ignora erro silenciosamente
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const tagColorById = useMemo(() => {
    const map = {};
    tagOptions.forEach((tg) => {
      map[tg.id] = tg.color;
    });
    return map;
  }, [tagOptions]);

  // Funções para seleção múltipla CORRIGIDAS
  const handleSelectContact = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
    setSelectAllMode(false); // Desativa modo "todos da empresa" quando seleciona individualmente
  };

  const handleSelectAllContacts = () => {
    if (selectAllMode) {
      // Se já está em modo "todos", desmarcar tudo
      setSelectedContacts(new Set());
      setSelectAllMode(false);
    } else {
      // Verificar se todos da página atual estão selecionados
      const currentPageIds = contacts.map((contact) => contact.id);
      const allCurrentSelected = currentPageIds.every((id) => selectedContacts.has(id));

      if (allCurrentSelected && selectedContacts.size === currentPageIds.length) {
        // Se todos da página estão selecionados, ativar modo "todos da empresa"
        setSelectAllMode(true);
        setSelectedContacts(new Set()); // Limpar seleção individual
      } else {
        // Selecionar todos da página atual
        setSelectedContacts(new Set(currentPageIds));
        setSelectAllMode(false);
      }
    }
  };

  // Limpar seleção quando contatos mudam
  useEffect(() => {
    setSelectedContacts(new Set());
    setSelectAllMode(false);
  }, [searchParam, selectedTags]);

  // Função para obter contagem correta
  const getSelectedCount = () => {
    if (selectAllMode) {
      return totalContactsCount;
    }
    return selectedContacts.size;
  };

  // Função para obter texto do botão
  const getSelectionButtonText = () => {
    if (selectAllMode) {
      return `Excluir Todos (${totalContactsCount})`;
    }
    if (selectedContacts.size > 0) {
      return `Excluir Selecionados (${selectedContacts.size})`;
    }
    return "Excluir";
  };

  // Funções para exclusão múltipla CORRIGIDAS
  const handleBulkDeleteClick = () => {
    if (selectAllMode) {
      setDeleteType("all");
    } else if (selectedContacts.size > 0) {
      setDeleteType("selected");
    } else {
      toast.warning("Selecione pelo menos um contato para excluir");
      return;
    }
    setDeleteConfirmModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      if (deleteType === "all") {
        await api.delete("/contacts/all", {
          data: {
            confirmation: "DELETE_ALL_CONTACTS",
            excludeIds: [],
          },
        });
        toast.success(`Todos os ${totalContactsCount} contatos foram excluídos`);
      } else {
        const contactIds = Array.from(selectedContacts);
        await api.post("/contacts/bulk-delete", { contactIds });
        toast.success(`${contactIds.length} contatos excluídos com sucesso`);
      }

      // Resetar seleções
      setSelectedContacts(new Set());
      setSelectAllMode(false);

      // Atualizar lista
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteConfirmModalOpen(false);
      setDeleteType("");
    }
  };

  const handleDeleteAllClick = () => {
    setDeleteType("all");
    setDeleteConfirmModalOpen(true);
  };

  // Função para abrir modal da imagem
  const handleOpenImageModal = (imageUrl, contactName) => {
    setSelectedImage(imageUrl);
    setSelectedContactName(contactName);
    setImageModalOpen(true);
  };

  // Função para fechar modal da imagem
  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
    setSelectedContactName("");
  };

  const handleImportExcel = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `/contacts/upload`,
        method: "POST",
        data: formData,
      });
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  // /contacts?search=<texto> abre a lista já filtrada (é o link "Pode ser o mesmo cliente de:" do painel do contato
  // do webchat). Só age quando o parâmetro existe e reage à mudança dele com a página já aberta; sem ?search= nada muda.
  const { search: urlSearch } = useLocation();
  useEffect(() => {
    const fromUrl = new URLSearchParams(urlSearch).get("search");
    if (fromUrl) setSearchParam(fromUrl.toLowerCase());
  }, [urlSearch]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, selectedTags]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber,
              contactTag: JSON.stringify(selectedTags),
            },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setTotalContactsCount(data.count); // Armazenar contagem total
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, selectedTags]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContactEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setTotalContactsCount((prev) => Math.max(0, prev - 1));
      }

      if (data.action === "bulk-delete") {
        dispatch({ type: "BULK_DELETE_CONTACTS", payload: data.contactIds });
        setTotalContactsCount((prev) => Math.max(0, prev - data.contactIds.length));
      }

      if (data.action === "delete-all") {
        dispatch({ type: "DELETE_ALL_CONTACTS", payload: { excludeIds: data.excludeIds } });
        setTotalContactsCount(data.excludeIds.length);
      }
    };

    socket.on(`company-${companyId}-contact`, onContactEvent);

    return () => {
      socket.off(`company-${companyId}-contact`, onContactEvent);
    };
  }, [socket]);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  // O select múltiplo já devolve a lista de ids marcados (mesmo estado dos antigos chips)
  const handleTagsChange = (event) => {
    setSelectedTags(event.target.value);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: false });
      toast.success("Contato bloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setBlockingContact(null);
  };

  const handleUnBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: true });
      toast.success("Contato desbloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setUnBlockingContact(null);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
      setImportContacts(false);
    } catch (err) {
      toastError(err);
      setImportContacts(false);
    }
  };

  const handleimportChats = async () => {
    try {
      await api.post("/contacts/import/chats");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Determinar qual ação de confirmação executar para modais simples
  const getConfirmAction = () => {
    if (deletingContact) return () => handleDeleteContact(deletingContact.id);
    if (blockingContact) return () => handleBlockContact(blockingContact.id);
    if (unBlockingContact) return () => handleUnBlockContact(unBlockingContact.id);
    if (ImportContacts) return handleimportContact;
    return handleImportExcel;
  };

  // Determinar título do modal de confirmação simples
  const getConfirmTitle = () => {
    if (deletingContact) return `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`;
    if (blockingContact) return `Bloquear Contato ${blockingContact.name}?`;
    if (unBlockingContact) return `Desbloquear Contato ${unBlockingContact.name}?`;
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importTitlte");
    return i18n.t("contactListItems.confirmationModal.importTitlte");
  };

  // Determinar mensagem do modal de confirmação simples
  const getConfirmMessage = () => {
    if (exportContact) return i18n.t("contacts.confirmationModal.exportContact");
    if (deletingContact) return i18n.t("contacts.confirmationModal.deleteMessage");
    if (blockingContact) return i18n.t("contacts.confirmationModal.blockContact");
    if (unBlockingContact) return i18n.t("contacts.confirmationModal.unblockContact");
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importMessage");
    return i18n.t("contactListItems.confirmationModal.importMessage");
  };

  const selectedCount = getSelectedCount();
  const isAnyContactSelected = selectAllMode || selectedContacts.size > 0;

  // Status do checkbox "selecionar todos"
  const selectAllCheckboxStatus = () => {
    if (selectAllMode) return { checked: true, indeterminate: false };

    const currentPageIds = contacts.map((contact) => contact.id);
    const selectedInCurrentPage = currentPageIds.filter((id) => selectedContacts.has(id)).length;

    if (selectedInCurrentPage === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedInCurrentPage === currentPageIds.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const checkboxStatus = selectAllCheckboxStatus();

  // Filtros de canal/carteira são aplicados sobre a lista carregada (a API filtra só busca e tags)
  const visibleContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (channelFilter && (c.channel || "whatsapp") !== channelFilter) return false;
      if (walletFilter) {
        const wid = c.contactWallets && c.contactWallets[0] && c.contactWallets[0].wallet?.id;
        if (wid !== walletFilter) return false;
      }
      return true;
    });
  }, [contacts, channelFilter, walletFilter]);

  const hasAnyFilter = selectedTags.length > 0 || !!channelFilter || !!walletFilter;

  const formatNumber = (contact) => {
    if (contact?.channel === "webchat") return "Webchat";
    if (enableLGPD && hideNum && user.profile === "user") {
      if (contact.isGroup) return contact.number;
      const serialized = formatSerializedId(contact?.number);
      return serialized === null
        ? contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2)
        : serialized?.slice(0, -6) + "**-**" + contact?.number?.slice(-2);
    }
    return contact.isGroup ? contact.number : formatSerializedId(contact?.number);
  };

  const walletName = (contact) =>
    contact.contactWallets && contact.contactWallets.length > 0
      ? contact.contactWallets[0].wallet?.name || "Usuário não encontrado"
      : null;

  const renderChannel = (contact) => {
    const ch = CHANNELS.find((c) => c.key === (contact.channel || "whatsapp")) || CHANNELS[0];
    const Icon = ch.Icon;
    return (
      <span className={classes.channel}>
        <Icon />
        {ch.label}
      </span>
    );
  };

  // Chip com a cor da tag (dado); o texto usa o contraste calculado pelo tema
  const tagChipStyle = (color) => {
    if (!color) return undefined;
    try {
      return { backgroundColor: color, color: theme.palette.getContrastText(color) };
    } catch (e) {
      return undefined;
    }
  };

  const COLS = 8;

  const renderSkeletonRows = (n = 4) =>
    Array.from({ length: n }, (_, i) => (
      <TableRow key={`sk-${i}`}>
        <TableCell className={classes.checkboxCell}>
          <Skeleton variant="rect" width={16} height={16} style={{ borderRadius: 4, marginLeft: 6 }} />
        </TableCell>
        <TableCell>
          <div className={classes.contactCell}>
            <Skeleton variant="circle" width={32} height={32} />
            <div>
              <Skeleton width={140} height={16} />
              <Skeleton width={100} height={14} />
            </div>
          </div>
        </TableCell>
        <TableCell><Skeleton width={160} height={16} /></TableCell>
        <TableCell><Skeleton width={90} height={20} style={{ borderRadius: 6 }} /></TableCell>
        <TableCell><Skeleton width={90} height={16} /></TableCell>
        <TableCell><Skeleton width={100} height={16} /></TableCell>
        <TableCell><Skeleton width={70} height={20} style={{ borderRadius: 6 }} /></TableCell>
        <TableCell />
      </TableRow>
    ));

  const headerActions = [
    <Button
      key="export"
      variant="outlined"
      startIcon={<Backup fontSize="small" />}
      onClick={() => setImportContactModalOpen(true)}
    >
      Exportar {/* TODO i18n */}
    </Button>,
    <PopupState key="import" variant="popover" popupId="contacts-import-menu">
      {(popupState) => (
        <React.Fragment>
          <Button variant="outlined" endIcon={<ArrowDropDown />} {...bindTrigger(popupState)}>
            Importar {/* TODO i18n */}
          </Button>
          <Menu
            {...bindMenu(popupState)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setConfirmOpen(true);
                setImportContacts(true);
                popupState.close();
              }}
            >
              <ContactPhone fontSize="small" />
              {i18n.t("contacts.menu.importYourPhone")}
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setImportContactModalOpen(true);
                popupState.close();
              }}
            >
              <Backup fontSize="small" />
              {i18n.t("contacts.menu.importToExcel")}
            </MenuItem>
            <Can
              role={user.profile}
              perform="contacts-page:deleteAllContacts"
              yes={() => (
                <MenuItem
                  className={clsx(classes.menuItem, classes.menuItemDanger)}
                  onClick={() => {
                    handleDeleteAllClick();
                    popupState.close();
                  }}
                >
                  <DeleteSweep fontSize="small" />
                  Excluir Todos os Contatos
                </MenuItem>
              )}
            />
          </Menu>
        </React.Fragment>
      )}
    </PopupState>,
    <Button
      key="new"
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={handleOpenContactModal}
    >
      Novo contato {/* TODO i18n */}
    </Button>,
  ];

  const selectMenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: { style: { maxHeight: 360 } },
  };

  return (
    <MainContainer maxWidth>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>

      {/* Modal de confirmação tipada para exclusão */}
      <ContactDeleteConfirmModal
        open={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        deleteType={deleteType}
        selectedCount={selectedContacts.size}
        totalCount={totalContactsCount}
      />

      {/* Modal para visualizar imagem de perfil */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        maxWidth="xs"
        fullWidth
        classes={{ paper: p.dialogPaper }}
      >
        <ModalTitle title={selectedContactName || "Foto do contato"} onClose={handleCloseImageModal} />
        <DialogContent className={classes.imageBody}>
          {selectedImage ? (
            <img
              src={selectedImage}
              alt={`Foto de perfil de ${selectedContactName}`}
              className={classes.profileImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className={classes.imageFallback}>Imagem não disponível</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação simples para outras ações */}
      <ConfirmationModal
        title={getConfirmTitle()}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={getConfirmAction()}
      >
        {getConfirmMessage()}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("contacts.confirmationModal.importChat")}
        open={confirmChatsOpen}
        onClose={setConfirmChatsOpen}
        onConfirm={(e) => handleimportChats()}
      >
        {i18n.t("contacts.confirmationModal.wantImport")}
      </ConfirmationModal>

      {importContactModalOpen && (
        <ContactImportWpModal
          isOpen={importContactModalOpen}
          handleClose={() => setImportContactModalOpen(false)}
          selectedTags={selectedTags}
          hideNum={hideNum}
          userProfile={user.profile}
        />
      )}

      {/* Bloquear/desbloquear e excluir da linha (menu "⋮") */}
      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {rowMenu.contact && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              const contact = rowMenu.contact;
              closeRowMenu();
              setConfirmOpen(true);
              if (contact.active) setBlockingContact(contact);
              else setUnBlockingContact(contact);
            }}
          >
            {rowMenu.contact.active ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" />
            )}
            {rowMenu.contact.active ? "Bloquear" : "Desbloquear"}
          </MenuItem>
        )}
        <Can
          role={user.profile}
          perform="contacts-page:deleteContact"
          yes={() => (
            <MenuItem
              className={clsx(classes.menuItem, classes.menuItemDanger)}
              onClick={() => {
                const contact = rowMenu.contact;
                closeRowMenu();
                setConfirmOpen(true);
                setDeletingContact(contact);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              Excluir
            </MenuItem>
          )}
        />
      </Menu>

      <PageHeader
        title={i18n.t("contacts.title")}
        subtitle="Clientes e leads da empresa, com tags, canal e carteira."
        badge={
          <StatusBadge
            color="neutral"
            dot={false}
            label={`${totalContactsCount}`}
          />
        }
        actions={headerActions}
      />

      {/* Busca + filtros */}
      <FilterBar>
        <SearchField
          value={searchParam}
          onChange={handleSearch}
          placeholder={i18n.t("contacts.searchPlaceholder")}
        />

        {tagOptions.length > 0 && (
          <TextField
            select
            size="small"
            variant="outlined"
            className={classes.filterSelect}
            value={selectedTags}
            onChange={handleTagsChange}
            inputProps={{ "aria-label": "Filtrar por tags" }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              MenuProps: selectMenuProps,
              SelectDisplayProps: { "aria-label": "Filtrar por tags" },
              renderValue: (selected) => {
                if (!selected || selected.length === 0) {
                  return <span className={classes.placeholder}>Todas as tags</span>;
                }
                if (selected.length > 1) return `${selected.length} tags`;
                const tg = tagOptions.find((o) => o.id === selected[0]);
                return (
                  <span className={classes.selectValue}>
                    <span
                      className={p.colorDot}
                      style={{ backgroundColor: (tg && tg.color) || tokens.textMuted }}
                    />
                    <span>{tg ? tg.name : "1 tag"}</span>
                  </span>
                );
              },
            }}
          >
            {tagOptions.map((tg) => (
              <MenuItem key={tg.id} value={tg.id} className={classes.optionItem}>
                <Checkbox size="small" color="primary" checked={selectedTags.includes(tg.id)} />
                <span
                  className={p.colorDot}
                  style={{ backgroundColor: tg.color || tokens.textMuted }}
                />
                {tg.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          size="small"
          variant="outlined"
          className={classes.filterSelect}
          value={channelFilter || ""}
          onChange={(e) => setChannelFilter(e.target.value || null)}
          inputProps={{ "aria-label": "Filtrar por canal" }}
          SelectProps={{
            displayEmpty: true,
            MenuProps: selectMenuProps,
            SelectDisplayProps: { "aria-label": "Filtrar por canal" },
            renderValue: (value) => {
              const ch = CHANNELS.find((c) => c.key === value);
              if (!ch) return <span className={classes.placeholder}>Todos os canais</span>;
              const Icon = ch.Icon;
              return (
                <span className={classes.selectValue}>
                  <Icon />
                  <span>{ch.label}</span>
                </span>
              );
            },
          }}
        >
          <MenuItem value="">Todos os canais</MenuItem>
          {CHANNELS.map((ch) => {
            const Icon = ch.Icon;
            return (
              <MenuItem key={ch.key} value={ch.key} className={classes.optionItem}>
                <Icon />
                {ch.label}
              </MenuItem>
            );
          })}
        </TextField>

        {walletOptions.length > 0 && (
          <TextField
            select
            size="small"
            variant="outlined"
            className={classes.filterSelect}
            value={walletFilter || ""}
            onChange={(e) => setWalletFilter(e.target.value || null)}
            inputProps={{ "aria-label": "Filtrar por carteira" }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: selectMenuProps,
              SelectDisplayProps: { "aria-label": "Filtrar por carteira" },
              renderValue: (value) => {
                const w = walletOptions.find((o) => o.id === value);
                if (!w) return <span className={classes.placeholder}>Todas as carteiras</span>;
                return w.name;
              },
            }}
          >
            <MenuItem value="">Todas as carteiras</MenuItem>
            {walletOptions.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {hasAnyFilter && (
          <Button
            onClick={() => {
              setSelectedTags([]);
              setChannelFilter(null);
              setWalletFilter(null);
            }}
          >
            Limpar filtros {/* TODO i18n */}
          </Button>
        )}
      </FilterBar>

      {/* Barra de seleção em lote */}
      {isAnyContactSelected && (
        <div className={classes.selectionBar}>
          <span className={classes.selectionText}>
            {selectedCount} contato(s) selecionado(s) {selectAllMode && "(TODOS OS CONTATOS)"}
          </span>
          <Button
            size="small"
            onClick={() => {
              setSelectedContacts(new Set());
              setSelectAllMode(false);
            }}
          >
            Cancelar
          </Button>
          <Tooltip title={selectAllMode ? "Excluir todos os contatos" : "Excluir contatos selecionados"}>
            <DangerButton
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleBulkDeleteClick}
            >
              {getSelectionButtonText()}
            </DangerButton>
          </Tooltip>
        </div>
      )}

      <input
        style={{ display: "none" }}
        id="upload"
        name="file"
        type="file"
        accept=".xls,.xlsx"
        onChange={() => {
          setConfirmOpen(true);
        }}
        ref={fileUploadRef}
      />

      <TableCard fill onScroll={handleScroll}>
        <Table stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  color="primary"
                  indeterminate={checkboxStatus.indeterminate}
                  checked={checkboxStatus.checked}
                  onChange={handleSelectAllContacts}
                  disabled={contacts.length === 0}
                  inputProps={{ "aria-label": "Selecionar todos os contatos" }}
                />
              </TableCell>
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell>{i18n.t("contacts.table.email")}</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Canal</TableCell>
              <TableCell>{i18n.t("contacts.table.wallet")}</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">{i18n.t("contacts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && visibleContacts.length === 0 && (
              <TableRow style={{ backgroundColor: "transparent" }}>
                <TableCell colSpan={COLS} style={{ whiteSpace: "normal" }}>
                  <EmptyState
                    icon={<PeopleOutlineIcon />}
                    title={hasAnyFilter || searchParam ? "Nenhum contato corresponde aos filtros" : "Nenhum contato encontrado"}
                    message={
                      hasAnyFilter || searchParam
                        ? "Ajuste a busca ou limpe os filtros para ver mais resultados."
                        : "Adicione um contato pelo botão acima ou importe sua lista para começar a atender."
                    }
                    action={
                      !(hasAnyFilter || searchParam) ? (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleOpenContactModal}
                        >
                          Novo contato {/* TODO i18n */}
                        </Button>
                      ) : undefined
                    }
                  />
                </TableCell>
              </TableRow>
            )}

            {visibleContacts.map((contact) => {
              const isSelected = selectAllMode || selectedContacts.has(contact.id);
              const wallet = walletName(contact);
              const tags = Array.isArray(contact.tags) ? contact.tags : [];

              return (
                <TableRow key={contact.id} selected={isSelected}>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      color="primary"
                      checked={isSelected}
                      onChange={() => handleSelectContact(contact.id)}
                      disabled={selectAllMode}
                      inputProps={{ "aria-label": `Selecionar contato ${contact.name}` }}
                    />
                  </TableCell>

                  <TableCell>
                    <div className={classes.contactCell}>
                      <Avatar
                        src={contact?.urlPicture || undefined}
                        className={classes.avatar}
                        onClick={() => handleOpenImageModal(contact?.urlPicture, contact.name)}
                      >
                        {initials(contact.name)}
                      </Avatar>
                      <div style={{ minWidth: 0 }}>
                        <div className={classes.contactName} title={`#${contact.id} · ${contact.name}`}>
                          {contact.name}
                        </div>
                        <div className={classes.contactPhone}>{formatNumber(contact)}</div>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    {contact.email ? (
                      <span className={classes.ellipsis} title={contact.email}>
                        {contact.email}
                      </span>
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {tags.length > 0 ? (
                      <div className={classes.tagsCell}>
                        {tags.slice(0, 2).map((tg) => (
                          <Chip
                            key={tg.id}
                            size="small"
                            label={tg.name}
                            className={classes.tagChip}
                            style={tagChipStyle(tg.color || tagColorById[tg.id])}
                          />
                        ))}
                        {tags.length > 2 && (
                          <Tooltip title={tags.slice(2).map((tg) => tg.name).join(", ")}>
                            <span>
                              <StatusBadge color="neutral" dot={false} label={`+${tags.length - 2}`} />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>

                  <TableCell>{renderChannel(contact)}</TableCell>

                  <TableCell>
                    {wallet ? (
                      <span className={classes.ellipsis}>{wallet}</span>
                    ) : (
                      <span className={classes.muted}>Não atribuído</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {contact.active ? (
                      <StatusBadge color="success" label="Ativo" />
                    ) : (
                      <StatusBadge color="error" label="Bloqueado" />
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <RowActions>
                      <Tooltip title="Iniciar atendimento">
                        <span>
                          <IconButton
                            aria-label="Iniciar atendimento"
                            disabled={!contact.active}
                            onClick={() => {
                              setContactTicket(contact);
                              setNewTicketModalOpen(true);
                            }}
                          >
                            <ChatOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Editar">
                        <IconButton aria-label="Editar" onClick={() => hadleEditContact(contact.id)}>
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Mais ações">
                        <IconButton
                          aria-label="Mais ações do contato"
                          aria-haspopup="true"
                          onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, contact })}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}

            {loading && renderSkeletonRows(contacts.length === 0 ? 6 : 2)}
          </TableBody>
        </Table>
      </TableCard>
    </MainContainer>
  );
};

export default Contacts;
